// webSerial/WebSerial.js

import { sleep } from '@/util';

/** Cobbeled together from
 *
 * https://github.com/rwaldron/johnny-five/blob/main/lib/board.pins.js
 * https://github.com/firmata/arduino/blob/main/Boards.h
 */
export let translatePins = {
  A0: 54,
  A1: 55,
  A2: 56,
  A3: 57,
  A4: 58,
  A5: 59,
  A6: 60,
  A7: 61,
  A8: 62,
  A9: 63,
  A10: 64,
  A11: 65,
  A12: 66,
  A13: 67,
  A14: 68,
  A15: 69,
  I16: 38,
  I17: 39,
  I18: 40,
  IN0: 18,
  IN1: 19,
  D0: 2,
  D1: 3,
  D2: 4,
  D3: 5,
  D4: 6,
  D5: 7,
  D6: 8,
  D7: 9,
  D8: 10,
  D9: 11,
  D10: 12,
  D11: 13,
  D12: 42,
  D13: 43,
  D14: 44,
  D15: 45,
  D16: 46,
  D17: 47,
  D18: 48,
  D19: 49,
  //D20: "PD4",
  //D21: "PD5",
  //D22: "PD6",
  //D23: "PJ4",
  R0: 22,
  R1: 23,
  R2: 24,
  R3: 25,
  R4: 26,
  R5: 27,
  R6: 28,
  R7: 29,
  R8: 30,
  R9: 31,
  R10: 32,
  R11: 33,
  R12: 34,
  R13: 35,
  R14: 36,
  R15: 37,
  TX3: 14,
  RX3: 15,
};

let channels = [0x00, 0x40, 0x10, 0x50, 0x20, 0x60, 0x30, 0x70];
let addresses = [0x00, 0x01, 0x02]; //0x03

let psensors = [];
let i = 0;
for (let address of addresses) {
  for (let channel of channels) {
    psensors.push({
      address: 0x48 ^ address,
      command: 0x88 ^ channel,
      name: 'P' + i,
    });
    i++;
  }
}

import SerialPort from './SerialPort';
import { Firmata } from 'firmata-io';

// Custom sysex commands
const OVL_SWITCH = 0x05;
const CONTROLLINO_D20_PLUS = 0x06;
const CONTROLLINO_PWM_FREQ = 0x07;
const I2C_REGISTER_NOT_SPECIFIED = -1;
const RESI_DI = 0x08;
const PC_READY = 0x09;

const SENSOR_DELAY = 50;

Firmata.SYSEX_RESPONSE[OVL_SWITCH] = function (board) {
  let state = board.buffer[2];
  board.emit('overload', state);
};

function setBit(buffer, i, bit, value) {
  if (value == 0) {
    buffer[i] &= ~(1 << bit);
  } else {
    buffer[i] |= 1 << bit;
  }
}

const DIGITAL_TRESHOLD = 12;
// Hysteresis band around DIGITAL_TRESHOLD: once ON, the signal must drop
// below (DIGITAL_TRESHOLD - DIGITAL_HYSTERESIS) to go OFF again, instead of
// re-crossing the same single cutoff. Without this, a noisy analog reading
// hovering near the threshold (e.g. on A0-A6) flips the digital state back
// and forth on every small fluctuation.
const DIGITAL_HYSTERESIS = 2;
// Minimum time between two parent.setAnalog()/setDigital() calls for the
// same pin. Excess updates within the window are not dropped — the latest
// value is buffered and flushed via a trailing timer once the window
// elapses, so the last state is never missed, just delayed.
const UPDATE_THROTTLE_MS = 100;
// Repeat a changed output once after the same interval. The repeat is only
// sent when the value is still current, giving the box one recovery write
// without returning to continuously rewriting unchanged outputs.
const OUTPUT_REPEAT_DELAY_MS = UPDATE_THROTTLE_MS;
// Box-question evaluation waits beyond the trailing throttle window so the
// final input in a test step is visible before it is corrected.
const INPUT_SETTLE_DELAY_MS = 250;
const EPSILON = 0.02;

class WebSerial {
  lastBits = new Array(64).fill(false);
  masks = [1, 2, 4, 8, 10, 20, 40, 80];

  // name -> { lastEmit, timer, value } — per-pin rate limiting for
  // parent.setAnalog()/setDigital(), see throttledEmit().
  analogThrottle = Object.create(null);
  digitalThrottle = Object.create(null);
  latestDigitalInputs = Object.create(null);

  // `${type}:${name}` -> serialized value last sent to the board.
  // sync() runs for every parent state update, so without this cache it
  // rewrites every output even when only one value changed.
  lastEmittedValues = Object.create(null);
  outputRepeatTimers = Object.create(null);

  connecting = false;
  disconnecting = false;

  // Servo movement smoothing.
  //
  // Normal hobby servos do not support "speed" directly through Firmata.
  // So we move slowly by sending small intermediate angle commands.
  servoPositions = Object.create(null);
  servoTargets = Object.create(null);
  servoMoveTokens = Object.create(null);
  servoDefaultSpeedDegPerSec = 45;
  servoStepMs = 20;

  // periodic “re-read all inputs”
  inputRefreshInterval = null;
  inputRefreshDelay = 5000; // ~ every 5s
  refreshingInputs = false;

  isPortOpen(port) {
    // Web Serial doesn’t expose a clean “isOpen”, but open ports have readable/writable streams.
    return !!(port && (port.readable || port.writable));
  }

  constructor(parent) {
    const _this = this;
    this.parent = parent;

    this.pinModes = Object.create(null);
    this.synced = Object.create(null);

    this.subscriptions = [];
    this.subscriptions.push(
      _this.parent.onState.subscribe(async () => {
        _this.sync();
      })
    );

    this.currentSensorIndex = 0;
    this.scheduleTimeout = null;
    this.heartbeatInterval = null;
    this.heartbeatDelay = 5000;

    this.port = null; // Web Serial port
    this.serialPort = null; // your SerialPort wrapper
    this.board = null;

    Firmata.SYSEX_RESPONSE[RESI_DI] = function (board) {
      let bits = new Array(64).fill(false);
      const buf = Buffer.from(board.buffer.slice(5));
      const lost_bits1 = board.buffer[3];
      const lost_bits2 = board.buffer[4];
      setBit(buf, 0, 7, lost_bits1 & 1);
      setBit(buf, 1, 7, lost_bits1 & 2);
      setBit(buf, 2, 7, lost_bits1 & 4);
      setBit(buf, 3, 7, lost_bits1 & 8);
      setBit(buf, 4, 7, lost_bits1 & 16);
      setBit(buf, 5, 7, lost_bits1 & 32);
      setBit(buf, 6, 7, lost_bits1 & 64);
      setBit(buf, 7, 7, lost_bits2 & 1);

      for (let i = 0; i < 4; i++) {
        let uintdata = buf.readUInt16LE(2 * i);
        for (let j = 0; j < 16; j++) {
          bits[i * 16 + j] = (uintdata & Math.pow(2, j)) !== 0;
          if (bits[i * 16 + j] !== _this.lastBits[i * 16 + j]) {
            _this.parseAnalog(`IR${1 + i * 16 + j}`, bits[i * 16 + j] * 24);
          }
        }
      }
      _this.lastBits = bits;
    };
  }

  async refreshAllInputs(reason = 'refresh') {
    if (!this.board) return false;
    if (this.refreshingInputs) return false;

    this.refreshingInputs = true;
    try {
      // analogRead()/digitalRead() add EventEmitter listeners, so calling them
      // on every refresh leaks callbacks. Re-enable Firmata reporting while
      // keeping the single listeners that sync() registered initially.
      for (const pin of Object.values(this.parent.state)) {
        const pinName = translatePins[pin.name];
        if (pinName == null) continue;

        if (pin.type === 'analog_input') {
          this.board.reportAnalogPin(pinName - 54, 1);
        } else if (pin.type === 'digital_input') {
          this.board.reportDigitalPin(pinName, 1);
        }
      }
      return true;
    } catch (e) {
      console.warn(`refreshAllInputs failed (${reason})`, e);
      return false;
    } finally {
      this.refreshingInputs = false;
    }
  }

  startInputRefresh() {
    this.stopInputRefresh();
    this.inputRefreshInterval = setInterval(async () => {
      if (!this.board) return;
      if (this.connecting || this.disconnecting) return;
      await this.refreshAllInputs('interval');
    }, this.inputRefreshDelay);
  }

  stopInputRefresh() {
    if (this.inputRefreshInterval) {
      clearInterval(this.inputRefreshInterval);
      this.inputRefreshInterval = null;
    }
  }

  startHeartbeat() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);

    this.heartbeatInterval = setInterval(async () => {
      if (!this.board) return;
      try {
        // keep some traffic so dead links get detected
        await this.board.reportVersion(() => {});
      } catch (err) {
        console.error('🔴 Heartbeat failed:', err);
        await this.disconnect('heartbeat failed');
      }
    }, this.heartbeatDelay);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  cleanUp() {
    return this.disconnect('cleanup');
  }

  reconnecting = false;
  retryCount = 0;
  maxRetries = 5;

  reconnect(delay = 1000) {
    if (this.reconnecting || this.retryCount >= this.maxRetries) return;

    this.reconnecting = true;
    setTimeout(() => {
      console.warn(
        `🔁 Attempting reconnect (${this.retryCount + 1}/${this.maxRetries})`
      );

      this.cleanUp();

      this.Connect()
        .then(() => {
          this.reconnecting = false;
          this.retryCount = 0;
        })
        .catch(() => {
          this.reconnecting = false;
          this.retryCount++;
          if (this.retryCount >= this.maxRetries) {
            console.error(
              'Reconnect failed too many times. Waiting for manual retry.'
            );
            if (this.parent.onConnectionFailed) {
              this.parent.onConnectionFailed.next(true);
            }
            return;
          }
          this.reconnect(Math.min(delay + 500, 5000));
        });
    }, delay);
  }

  Connect = async () => {
    if (this.connecting) return false;
    this.connecting = true;

    const _this = this;

    try {
      await this.disconnect('connect start');

      const filter = { usbVendorId: 0x2341 };

      if (!('serial' in navigator)) {
        throw new Error('Web Serial API not available');
      }

      let port = null;
      try {
        const ports = await navigator.serial.getPorts();
        const matches =
          ports?.filter((p) => {
            try {
              const info = p.getInfo?.();
              return info?.usbVendorId === filter.usbVendorId;
            } catch {
              return false;
            }
          }) ?? [];

        if (matches.length === 1) port = matches[0];
      } catch {
        // ignore
      }

      if (!port) {
        port = await navigator.serial.requestPort({ filters: [filter] });
      }

      this.port = port;

      await sleep(50);

      if (!this.isPortOpen(this.port)) {
        await this.port.open({ baudRate: 250000 });
      }

      const serialPort = new SerialPort(this.port, {
        onError: async () => {
          _this.parent.onInitialize.next(false);
          _this.parent.boxConnected.next(false);
          await _this.disconnect('serialport onError');
        },
      });

      this.serialPort = serialPort;
      this.board = new Firmata(serialPort);

      const readyPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error('Firmata ready timeout'));
        }, 8000);

        const cleanup = () => {
          clearTimeout(timeout);
          try {
            _this.board?.off?.('ready', onReady);
            _this.board?.off?.('close', onClose);
            _this.board?.off?.('error', onError);
          } catch {
            try {
              _this.board?.removeListener?.('ready', onReady);
              _this.board?.removeListener?.('close', onClose);
              _this.board?.removeListener?.('error', onError);
            } catch {
              // ignore
            }
          }
        };

        const onReady = async () => {
          cleanup();
          try {
            await _this.sync();

            _this.startInputRefresh();

            _this.board.sysexCommand([PC_READY]);
            _this.parent.boxConnected.next(true);
            _this.parent.onInitialize.next(true);
            _this.startHeartbeat();
            resolve(true);
          } catch (e) {
            reject(e);
          }
        };

        const onClose = async () => {
          cleanup();
          _this.parent.onInitialize.next(false);
          _this.parent.boxConnected.next(false);
          await _this.disconnect('board close');
          reject(new Error('Board closed'));
        };

        const onError = async (err) => {
          cleanup();
          _this.parent.onInitialize.next(false);
          _this.parent.boxConnected.next(false);
          await _this.disconnect('board error');
          reject(err || new Error('Board error'));
        };

        _this.board.on('ready', onReady);
        _this.board.on('close', onClose);
        _this.board.on('error', onError);
      });

      await serialPort.open();
      await readyPromise;

      return true;
    } catch (err) {
      console.error('Error opening serial port:', err);
      this.parent.onInitialize.next(false);
      this.parent.boxConnected.next(false);
      await this.disconnect('connect catch');
      throw err;
    } finally {
      this.connecting = false;
    }
  };

  schedule = () => {
    let _this = this;
    let sensor = _this.i2cs[_this.currentSensorIndex];
    if (!sensor)
      return (_this.scheduleTimeout = setTimeout(_this.schedule, SENSOR_DELAY));
    _this.board.i2cWrite(sensor.address, [sensor.command]);
    _this.board.i2cReadOnce(sensor.address, 2, async function (data) {
      let value = (data[0] << 8) | data[1];
      if (_this.parent.state[sensor.name]) {
        _this.parseAnalog(sensor.name, (value / 4095) * 5);
      }
      _this.currentSensorIndex++;
      _this.currentSensorIndex %= psensors.length;
      _this.scheduleTimeout = setTimeout(_this.schedule, SENSOR_DELAY);
    });
  };

  sync = async () => {
    if (!this.board) return;

    this.i2cs = [];

    for (let key of Object.keys(this.parent.state)) {
      let pin = this.parent.state[key];
      let pinName = translatePins[pin.name];
      if (!pinName && pin.type != 'i2c') continue;

      switch (pin.type) {
        case 'servo_output':
          this.emit('sendServo', pin.name, pin.value);
          break;

        case 'analog_input':
          if (!this.synced[pin.name]) {
            this.board.pinMode(pinName - 54, this.board.MODES.ANALOG);
            this.board.analogRead(pinName - 54, (val) => {
              this.parseAnalog(pin.name, (val / 800) * 24);
            });
            this.synced[pin.name] = true;
          }
          break;

        case 'digital_input': {
          if (this.pinModes[pinName] !== this.board.MODES.INPUT) {
            this.board.pinMode(pinName, this.board.MODES.INPUT);
            this.pinModes[pinName] = this.board.MODES.INPUT;
          }
          if (!this.synced[pin.name]) {
            this.board.digitalRead(pinName, (state) => {
              this.parseAnalog(pin.name, state * 24);
            });
            this.synced[pin.name] = true;
          }
          break;
        }

        case 'pwm_output':
          this.emit('sendAnalog', pin.name, pin.value);
          break;

        case 'digital_output':
        case 'relay_output':
          this.emit('sendDigital', pin.name, pin.state);
          break;
      }
    }
  };

  async moveServoSlow(name, value) {
    const pin = translatePins[name];
    if (pin == null || !this.board) return;

    const target =
      typeof value === 'object' && value !== null
        ? value.angle ?? value.value ?? value.target
        : value;

    const speedDegPerSec =
      typeof value === 'object' && value !== null && value.speedDegPerSec
        ? value.speedDegPerSec
        : this.servoDefaultSpeedDegPerSec;

    const stepMs =
      typeof value === 'object' && value !== null && value.stepMs
        ? value.stepMs
        : this.servoStepMs;

    const targetAngle = Number(target);
    if (!Number.isFinite(targetAngle)) return;

    const clampedTarget = Math.max(0, Math.min(180, targetAngle));

    if (this.pinModes[pin] !== this.board.MODES.SERVO) {
      this.board.pinMode(pin, this.board.MODES.SERVO);
      this.pinModes[pin] = this.board.MODES.SERVO;
    }

    // If this servo is already moving to this exact target, do not start
    // another identical movement loop.
    if (
      this.servoTargets[name] === clampedTarget &&
      this.servoMoveTokens[name]
    ) {
      return;
    }

    this.servoTargets[name] = clampedTarget;

    // First command after connect/reconnect: we do not know the physical
    // servo position, so jump once and remember the commanded position.
    if (!Number.isFinite(this.servoPositions[name])) {
      this.board.servoWrite(pin, clampedTarget);
      this.servoPositions[name] = clampedTarget;
      return;
    }

    if (Math.abs(this.servoPositions[name] - clampedTarget) <= 0.01) {
      return;
    }

    const token = Symbol('servoMove');
    this.servoMoveTokens[name] = token;

    let current = this.servoPositions[name];

    const speed = Math.max(1, Number(speedDegPerSec) || 45);
    const delay = Math.max(10, Number(stepMs) || 20);
    const step = Math.max(0.2, (speed * delay) / 1000);

    while (Math.abs(current - clampedTarget) > 0.01) {
      if (this.servoMoveTokens[name] !== token) return;
      if (!this.board) return;

      const direction = clampedTarget > current ? 1 : -1;
      const next =
        Math.abs(clampedTarget - current) <= step
          ? clampedTarget
          : current + direction * step;

      this.board.servoWrite(pin, next);
      this.servoPositions[name] = next;
      current = next;

      await sleep(delay);
    }

    if (this.servoMoveTokens[name] === token) {
      delete this.servoMoveTokens[name];
    }
  }

  valueSignature(value) {
    if (typeof value === 'number' && Number.isNaN(value)) return 'number:NaN';

    try {
      return `${typeof value}:${JSON.stringify(value)}`;
    } catch {
      return `${typeof value}:${String(value)}`;
    }
  }

  clearOutputRepeat(cacheKey) {
    if (!this.outputRepeatTimers[cacheKey]) return;
    clearTimeout(this.outputRepeatTimers[cacheKey]);
    delete this.outputRepeatTimers[cacheKey];
  }

  scheduleOutputRepeat(type, name, value, valueSignature) {
    const cacheKey = `${type}:${name}`;
    this.clearOutputRepeat(cacheKey);

    this.outputRepeatTimers[cacheKey] = setTimeout(() => {
      delete this.outputRepeatTimers[cacheKey];

      if (!this.board) return;
      if (this.lastEmittedValues[cacheKey] !== valueSignature) return;

      this.emit(type, name, value, {
        repeat: true,
        expectedSignature: valueSignature,
      });
    }, OUTPUT_REPEAT_DELAY_MS);
  }

  waitForInputSettle = async () => {
    await sleep(INPUT_SETTLE_DELAY_MS);
  };

  emit = async (type, name, value, options = {}) => {
    const { repeat = false, expectedSignature } = options;
    const cacheKey = `${type}:${name}`;
    const valueSignature = this.valueSignature(value);
    const hadPreviousValue = Object.prototype.hasOwnProperty.call(
      this.lastEmittedValues,
      cacheKey
    );
    const previousValue = this.lastEmittedValues[cacheKey];

    if (repeat) {
      if (this.lastEmittedValues[cacheKey] !== expectedSignature) return;
      if (valueSignature !== expectedSignature) return;
    } else if (hadPreviousValue && previousValue === valueSignature) {
      return;
    }

    // These commands all control the same physical output. A mode change
    // invalidates the other commands' cached values and pending repeats, so a
    // PWM reset cannot be skipped or overwritten after a digital fault.
    if (!repeat && ['sendServo', 'sendAnalog', 'sendDigital'].includes(type)) {
      for (const outputType of ['sendServo', 'sendAnalog', 'sendDigital']) {
        if (outputType === type) continue;
        const otherKey = `${outputType}:${name}`;
        delete this.lastEmittedValues[otherKey];
        this.clearOutputRepeat(otherKey);
      }
    }

    // Record before writing so concurrent sync calls cannot send the same
    // value while an asynchronous emit (such as servo movement) is pending.
    if (!repeat) {
      this.lastEmittedValues[cacheKey] = valueSignature;
      this.clearOutputRepeat(cacheKey);
    }
    // eslint-disable-next-line no-console
    console.warn(`emit: ${type} ${name} ${value}`);
    let pinName = translatePins[name];
    try {
      switch (type) {
        case 'sendServoConfig': {
          const pin = translatePins[name];
          const minUs = value?.minUs ?? 544;
          const maxUs = value?.maxUs ?? 2400;

          if (this.pinModes[pin] !== this.board.MODES.SERVO) {
            this.board.pinMode(pin, this.board.MODES.SERVO);
            this.pinModes[pin] = this.board.MODES.SERVO;
          }
          this.board.servoConfig(pin, minUs, maxUs);
          break;
        }

        case 'sendServo': {
          if (repeat && Number.isFinite(this.servoPositions[name])) {
            this.board.servoWrite(pinName, this.servoPositions[name]);
          } else {
            await this.moveServoSlow(name, value);
          }
          break;
        }

        case 'sendPwmFrequency':
          break;

        case 'sendAnalog':
          if (this.pinModes[pinName] !== this.board.MODES.PWM) {
            this.board.pinMode(pinName, this.board.MODES.PWM);
            this.pinModes[pinName] = this.board.MODES.PWM;
          }
          this.board.analogWrite(pinName, (value / 24) * 255);
          break;

        case 'sendDigital':
          switch (name) {
            case 'D20':
              this.board.sysexCommand([
                CONTROLLINO_D20_PLUS,
                value ? 0x01 : 0x02,
              ]);
              break;
            case 'D21':
              this.board.sysexCommand([
                CONTROLLINO_D20_PLUS,
                value ? 0x03 : 0x04,
              ]);
              break;
            case 'D22':
              this.board.sysexCommand([
                CONTROLLINO_D20_PLUS,
                value ? 0x05 : 0x06,
              ]);
              break;
            case 'D23':
              this.board.sysexCommand([
                CONTROLLINO_D20_PLUS,
                value ? 0x07 : 0x08,
              ]);
              break;
            default:
              if (this.board && pinName != null) {
                let val = (value / 24) * 800;
                if (isNaN(val)) val = false;
                if (this.pinModes[pinName] !== this.board.MODES.OUTPUT) {
                  this.board.pinMode(pinName, this.board.MODES.OUTPUT);
                  this.pinModes[pinName] = this.board.MODES.OUTPUT;
                }
                this.board.digitalWrite(pinName, val);
              }
          }
          break;

        case 'reset':
          break;

        default:
          console.warn('Unknown type: ', type);
          break;
      }

      if (
        !repeat &&
        ['sendServo', 'sendAnalog', 'sendDigital'].includes(type) &&
        this.lastEmittedValues[cacheKey] === valueSignature
      ) {
        this.scheduleOutputRepeat(type, name, value, valueSignature);
      }
    } catch (e) {
      // Allow the same value to be retried after a failed board write, but do
      // not overwrite a newer value queued while this emit was in progress.
      if (this.lastEmittedValues[cacheKey] === valueSignature) {
        if (hadPreviousValue) {
          this.lastEmittedValues[cacheKey] = previousValue;
        } else {
          delete this.lastEmittedValues[cacheKey];
        }
      }
      console.error(e);
    }
  };

  initWebSerial() {
    if ('serial' in navigator) {
      navigator.serial.addEventListener('connect', () => {
        this.parent.webConnected.next(false);
      });
      navigator.serial.addEventListener('disconnect', async () => {
        this.parent.webConnected.next(true);
        this.parent.onInitialize.next(false);
        await this.disconnect('navigator disconnect');
      });
    } else {
      console.error(
        "The Web serial API doesn't seem to be enabled in your browser."
      );
    }
  }

  // Rate-limits calls to `emit(name, value)` to at most once every
  // UPDATE_THROTTLE_MS per `name`, without dropping the latest value:
  //  - Outside the cooldown window (or first call): emits immediately.
  //  - Inside the window: buffers `value` as the pending one and — unless a
  //    flush is already scheduled — schedules it to fire once the window
  //    elapses. A later call within the same window just replaces the
  //    buffered value; the already-scheduled timer still fires on time and
  //    sends whatever the latest buffered value is by then.
  throttledEmit(store, name, value, emit) {
    const now = Date.now();
    const entry = store[name];

    if (!entry || now - entry.lastEmit >= UPDATE_THROTTLE_MS) {
      if (entry?.timer) clearTimeout(entry.timer);
      store[name] = { lastEmit: now, timer: null, value: undefined };
      emit(name, value);
      return;
    }

    entry.value = value;
    if (entry.timer) return; // a flush is already scheduled, it'll pick up the latest value

    const remaining = UPDATE_THROTTLE_MS - (now - entry.lastEmit);
    entry.timer = setTimeout(() => {
      entry.timer = null;
      entry.lastEmit = Date.now();
      emit(name, entry.value);
    }, remaining);
  }

  parseAnalog(name, val) {
    if (!this.parent.state[name]) this.parent.assertState(name);

    // Always pass the newest physical sample to the throttle. Comparing here
    // with the last published value can leave an older pending sample queued
    // when the signal returns to that published value inside the throttle
    // window.
    this.throttledEmit(this.analogThrottle, name, val, (n, v) => {
      const currentValue = this.parent.state[n].value ?? 0;
      if (
        Math.abs(currentValue - v) > EPSILON ||
        (v === 0 && currentValue !== 0)
      ) {
        console.warn(`analog ${n} ${v}`);
        this.parent.setAnalog(n, v);
      }
    });

    // Base hysteresis on the latest physical sample, not on the throttled UI
    // state. A LOW sample may still be queued when the signal returns HIGH.
    // Comparing against parent.state in that situation would ignore the new
    // HIGH sample and allow the older queued LOW to win.
    const currentDigital = Object.prototype.hasOwnProperty.call(
      this.latestDigitalInputs,
      name
    )
      ? this.latestDigitalInputs[name]
      : this.parent.state[name].state;
    const nextDigital = currentDigital
      ? val >= DIGITAL_TRESHOLD - DIGITAL_HYSTERESIS // already ON: needs to drop further to turn OFF
      : val >= DIGITAL_TRESHOLD; // already OFF: needs to reach the full threshold to turn ON

    this.latestDigitalInputs[name] = nextDigital;
    this.throttledEmit(this.digitalThrottle, name, nextDigital, (n, v) => {
      if (this.parent.state[n].state !== v) {
        console.warn(`digital ${n} ${v}`);
        this.parent.setDigital(n, v);
      }
    });
  }

  async disconnect(reason = 'disconnect') {
    if (this.disconnecting) return;
    this.disconnecting = true;

    try {
      this.stopHeartbeat();
      this.stopInputRefresh();

      if (this.scheduleTimeout) {
        clearTimeout(this.scheduleTimeout);
        this.scheduleTimeout = null;
      }

      for (const store of [this.analogThrottle, this.digitalThrottle]) {
        for (const name of Object.keys(store)) {
          if (store[name].timer) clearTimeout(store[name].timer);
        }
      }
      this.analogThrottle = Object.create(null);
      this.digitalThrottle = Object.create(null);
      this.latestDigitalInputs = Object.create(null);

      for (const cacheKey of Object.keys(this.outputRepeatTimers)) {
        this.clearOutputRepeat(cacheKey);
      }
      this.outputRepeatTimers = Object.create(null);
      this.lastEmittedValues = Object.create(null);

      try {
        this.board?.removeAllListeners?.();
      } catch {
        // ignore
      }

      try {
        await this.serialPort?.close?.();
      } catch {
        // ignore
      }
      this.serialPort = null;
      this.board = null;

      try {
        if (this.port && this.isPortOpen(this.port)) {
          await this.port.close();
        }
      } catch {
        // ignore
      }

      this.port = null;

      this.reconnecting = false;
      this.retryCount = 0;

      this.pinModes = Object.create(null);
      this.synced = Object.create(null);

      // Cancel any in-flight slow servo loops and forget commanded positions.
      // After reconnect we no longer know the real physical servo angle.
      this.servoMoveTokens = Object.create(null);
      this.servoTargets = Object.create(null);
      this.servoPositions = Object.create(null);

      this.parent.onInitialize?.next(false);
      this.parent.boxConnected?.next(false);
    } catch (e) {
      console.error('disconnect cleanup failed', e);
    } finally {
      this.disconnecting = false;
      this.connecting = false;
    }
  }
}

export default WebSerial;
